<template>
  <div class="Wdgt-Channels" @click.self="toClose">
    <div class="Wdgt-Channels__wrapper Anim__dropdown-wrapper">
      <div class="Wdgt-Channels__hdr">
        <div class="Wdgt-Channels__hdr-up">
          <TypographyTitle class="Wdgt-Channels__ttl" :level="'h3'">{{ get_ttlTxt }}</TypographyTitle>
          <ButtonClose class="Wdgt-Channels__close" @click="toClose"/>
        </div>
        <div class="Wdgt-Channels__search" @click="toFocusInput">
          <SearchIcon class="Wdgt-Channels__search-icon" />
          <input class="Wdgt-Channels__input" v-model="searchQuery" placeholder="Поиск" @input="toSearch" ref="textInput" /> 
        </div>
      </div>
      <div class="Wdgt-Channels__scroll-box" :class="get_gradStyles">
        <div class="Wdgt-Channels__list" @scroll="toCheckScroll" ref="scrollBox">
          <template v-if="get_channelsRenderStatus">
            <template v-for="channel in get_channels">
              <ChannelItem class="Wdgt-Channels__item" @click="toChooseMethod(channel)" :idChannel="channel.id" v-if="get_isShowChannelInList(channel)" />
            </template>
          </template>
          <template v-else>
            <div class="Wdgt-Channels__no-result-box">
              <SearchIcon />
              <span class="Wdgt-Channels__no-result-txt">Нет каналов по данному запросу</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
  import { useStore } from "vuex";
  import { debounce } from 'lodash';
  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
  //------------------------------------------
  import SearchIcon from '@svg/SearchIcon.vue';
  import ButtonClose from '@ui/Buttons/ButtonClose/ButtonClose.vue';
  import TypographyTitle from '@ui/Typography/TypographyTitle/TypographyTitle.vue';
  import ChannelItem from './components/ChannelItem/ChannelItem.vue';
  //------------------------------------------
  const store = useStore();
  //------------------------------------------
  const POPUP_NAME = DROPDOWN_NAMES.channels;
  //------------------------------------------
  const searchQuery = ref('');
  const isScrollTop = ref(false);
  const isScrollBottom = ref(false);
  const scrollBox = ref(null);
  const resizeObserver = ref({});
  const textInput = ref(null);
  //------------------------------------------
  //Получить список каналов
  const get_channels = computed(() => store.state.ContentSystem.channelsList);
  //Проверить, есть ли хотя бы один отрисованный канал в списке
  const get_channelsRenderStatus = computed(() => Object.values(get_channels.value)?.some(item => item.is_search_result));
  //Пользователь имеет права модератора?
  const get_isModerator = computed(() => store.getters['UserSystem/hasModeratorPrivileges']);
  //Отображать ли канал в списке
  const get_isShowChannelInList = (channel) => {
    return (
      channel.is_search_result &&
      (channel.isAdmin && get_isModerator.value || !channel.isAdmin)
    ) ? true : false;
  };
  //Включен режим добавления каналов
  const get_isActiveEditChannelMode = computed(() => store.state.ContentSystem.isEditChannels);
  //Выбрать текст заголовка дропдауна
  const get_ttlTxt = computed(() => get_isActiveEditChannelMode.value ? 'Добавить каналы' : 'Каналы');
  //Получть id поста, в котором будет изменяться список каналов
  const get_editPostId = computed(() => store.state.ContentSystem.idEditPostChannels)
  //Получить пост, в котором будет изменен список каналов
  const get_post = computed(() => store.state.ContentSystem.Alpha.list[get_editPostId.value]);
  //------------------------------------------
  onMounted(() => {
    toAddObserver();
    toCheckScroll();
  });
  onBeforeUnmount(() => {
    toRemoveObserver();
  });
  //------------------------------------------
  //Добавить классы с градиентом для границ скролл бокса
  const get_gradStyles = computed(() => {
    return `
      ${isScrollTop.value ? '' : 'Wdgt-Channels__scroll-box--top'}
      ${isScrollBottom.value ? '' : 'Wdgt-Channels__scroll-box--bottom'}
    `
  });
  //------------------------------------------
  //Выбрать дейсвие при клике по элементу списка
  const toChooseMethod = (channel) => {
    get_isActiveEditChannelMode.value ? toAddChannel(channel) : toActiveChannel(channel);
  }
  //Присвоить посту выбранный канал
  const toAddChannel = async (channel) => {
    const data = { 
      channel: channel,
      content_type: get_post.value.content_type,
      object_id: get_editPostId.value,
    }
    await store.dispatch('ContentSystem/Alpha/toChangeChannelsInPost', data);
  }
  //Активировать канал
  const toActiveChannel = (channel) => {
    if(store.state.ContentSystem.idChannelActive !== channel.id){
      store.commit('ContentSystem/toSetIdActiveChannel', channel.id);
      toLoadFilterdData();
    } else{
      store.commit('ContentSystem/toSetIdActiveChannel', null);
      store.dispatch('ContentSystem/toGetFirstPagePosts');
    }
    toClose();
  };
  //Загрузить данные по активироанному фильтру
  const toLoadFilterdData = debounce(async () => { store.dispatch('ContentSystem/toGetFilteredPosts') }, 700);
  //Активировать поиск 
  const toSearch = () => {
    for(const channel in get_channels.value){
      if(get_channels.value[channel].name.toLowerCase().includes(searchQuery.value.toLowerCase())){
        get_channels.value[channel].is_search_result = true;
      } else{
        get_channels.value[channel].is_search_result = false;
      }
    }
  };
  //Деактивировать результаты поиска
  const toClearSearch = () => {
    for(const channel in get_channels.value){
      get_channels.value[channel].is_search_result = true;
    }
  };
  //Закрыть дропдаун
  const toClose = () => {
    toClearSearch();
    store.dispatch('Dropdowns/removeTheDropdown', POPUP_NAME);
    store.commit('ContentSystem/toToggleChannelMode', { is_channels_mode: false, object_id: null });
  };
  //Добавить слушатель событий
  const toAddObserver = () => {
    resizeObserver.value = new ResizeObserver(toCheckScroll);
    resizeObserver.value.observe(scrollBox.value);
  };
  //Убрать слушатели событий
  const toRemoveObserver = () => {
    resizeObserver.value.disconnect();
  };
  //Проверить достиг ли скролл нижней или верхней позиции
  const toCheckScroll = () => {
    const box = scrollBox.value;
    if(!box) return;
    isScrollTop.value = (box.scrollTop <= 10) ? true : false;
    isScrollBottom.value = (box.scrollTop + box.clientHeight >= box.scrollHeight - 10) ? true : false;
  };
  //Сделать фокус на строку ввода
  const toFocusInput = () => {
    textInput.value.focus();
  }
</script>

<styles lang="scss">
  @import './styles/_conf';
</styles>